// src/shell/AppShell.jsx
import React, { useState, useCallback, useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X } from 'lucide-react';
import { useAuth } from '../auth/AuthContext.jsx';
import { API_BASE_URL } from '../services/api/config.js';
import { setFavicon, clearFavicon } from '../utils/setFavicon.js';
import Header from './Header.jsx';
import Sidebar from './Sidebar.jsx';
import AssistantWidget from './AssistantWidget.jsx';
import useOnboardingPoller from '../onboarding/useOnboardingPoller.js';
import OnboardingGate from '../onboarding/OnboardingGate.jsx';
import PersonalizationBanner from '../onboarding/ui/PersonalizationBanner.jsx';
import { COLORS } from '../constants/theme.js';

// Reactive CSS media query → boolean, so the shell can adapt its single toggle
// (drawer on mobile, collapse on desktop) without reading the viewport at click.
function useMediaQuery(query) {
  const getMatch = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false;
  const [matches, setMatches] = useState(getMatch);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mql = window.matchMedia(query);
    const onChange = (e) => setMatches(e.matches);
    // Safari <14 only has the deprecated addListener/removeListener.
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange);
    setMatches(mql.matches);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, [query]);
  return matches;
}

export default function AppShell() {
  const { phase, activeTenant, token, isTenantAdmin, isSuperadmin, adminMode } = useAuth();
  const { t } = useTranslation('shell');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Desktop sidebar collapses to an icon rail, toggled by pressing the logo.
  // Persisted per browser. Mobile keeps the drawer (open/closed) instead.
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('sidebar_collapsed') === '1');
  const isMobile = useMediaQuery('(max-width: 768px)');
  const location = useLocation();

  const toggleSidebar = useCallback(() => setSidebarOpen(v => !v), []);
  const closeSidebar = useCallback(() => setSidebarOpen(false), []);
  const toggleCollapsed = useCallback(() => setCollapsed(v => {
    const next = !v;
    localStorage.setItem('sidebar_collapsed', next ? '1' : '0');
    return next;
  }), []);

  // Close sidebar on navigation (mobile)
  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  // Set tab favicon from the active tenant; clear on unmount/logout.
  // The endpoint is public — browser fetches directly, no auth header needed.
  useEffect(() => {
    const faviconUrl = activeTenant?.favicon_url;
    if (!faviconUrl) return;
    setFavicon(`${API_BASE_URL}${faviconUrl}`);
    return () => clearFavicon();
  }, [activeTenant?.favicon_url]);

  // v191 — the personalization status poller. ⛔ Only for an admin of the ACTIVE workspace who is not a
  // superadmin (RequireTenantAdmin would bounce them from the wizard anyway), and only when 'ready'
  // (a 401 from a poll signs the user out). Called BEFORE the early returns: hooks are never conditional.
  useOnboardingPoller({
    enabled: phase === 'ready' && !!isTenantAdmin && !isSuperadmin && !adminMode,
    tenantId: activeTenant?.tenant_id || null,
    token,
  });

  if (phase === 'booting')     return null;
  if (phase === 'login')       return <Navigate to="/login" replace />;
  if (phase === 'no_tenants')  return <Navigate to="/no-access" replace />;
  if (phase === 'tenant_pick') return <Navigate to="/select-tenant" replace />;

  return (
    <>
      <style>{`
        /* 100dvh (with a 100vh fallback) so mobile browser chrome doesn't clip
           the bottom of the shell. */
        .app-shell { display: flex; flex-direction: row; height: 100vh; height: 100dvh; background: ${COLORS.bg}; position: relative; }
        .app-right { display: flex; flex-direction: column; flex: 1; min-width: 0; }
        .app-main  { flex: 1; overflow: auto; }

        /* Always-visible shell control: toggles the drawer on mobile and the
           collapse rail on desktop (guarantees an expand affordance on touch,
           where hover-peek is unavailable). */
        .shell-toggle-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          padding: 0;
          background: none;
          border: 1px solid ${COLORS.borderStrong};
          border-radius: 10px;
          cursor: pointer;
          color: ${COLORS.ink};
          flex-shrink: 0;
        }
        .shell-toggle-btn:hover { background: ${COLORS.field}; }

        .sidebar-overlay {
          display: none;
          position: fixed;
          inset: 0;
          background: rgba(35,0,62,0.4);
          z-index: 99;
        }

        @media (max-width: 768px) {
          .sidebar-overlay.open { display: block; }
        }
      `}</style>
      <div className="app-shell">
        <div className={`sidebar-overlay${sidebarOpen ? ' open' : ''}`} onClick={closeSidebar} />
        <Sidebar open={sidebarOpen} collapsed={collapsed} onToggleCollapse={toggleCollapsed} />
        {/* The content area: WizardModal centres its dialog here rather than in the whole window
            (`data-modal-area` = MODAL_AREA_ATTR in components/ui/modalArea.js). */}
        <div className="app-right" data-modal-area="">
          <OnboardingGate />
          <Header hamburgerBtn={
            // Mobile only: the drawer hamburger. On desktop the collapse/expand
            // control lives inside the sidebar itself.
            isMobile ? (
              <button
                type="button"
                className="shell-toggle-btn"
                onClick={toggleSidebar}
                aria-label={t('header.menu')}
                aria-expanded={sidebarOpen}
              >
                {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            ) : null
          } />
          {/* v191 — slim personalization bar; flex-shrink 0 so <main> stays the only scroller. */}
          <PersonalizationBanner />
          <main className="app-main">
            <Outlet />
          </main>
        </div>
        <AssistantWidget />
      </div>
    </>
  );
}
